import { FilterList as FilterListIcon, RestartAlt as ResetIcon } from '@mui/icons-material';
import { Box, Button, MenuItem, Select, Slider, Tooltip, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import React from 'react';
import { AiFillCalendar } from 'react-icons/ai';
import { GiLeg } from 'react-icons/gi';
import { TbSoccerField } from 'react-icons/tb';

import { demarcaciones, lateralidades } from '../../utils/UtilesComun';

/**
 * Selector estilizado con icono integrado para la barra de filtros.
 */
const FiltroSelectorBox = ({ icon, children, sx = {} }) => (
  <Box
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.75,
      height: 32,
      px: 1,
      backgroundColor: '#ffffff',
      border: '1px solid #cbd5e1',
      borderRadius: '6px',
      boxShadow: '0 1px 2px rgba(0, 0, 0, 0.03)',
      transition: 'all 0.15s ease-in-out',
      '&:hover': {
        borderColor: '#0f766e',
      },
      ...sx,
    }}
  >
    {icon && (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          color: '#0f766e',
          fontSize: '1rem',
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
    )}
    {children}
  </Box>
);

FiltroSelectorBox.propTypes = {
  icon: PropTypes.node,
  children: PropTypes.node,
  sx: PropTypes.object,
};

const selectCommonSx = {
  fontSize: '0.80rem',
  fontWeight: 600,
  backgroundColor: 'transparent',
  border: 'none',
  boxShadow: 'none',
  '&:hover': {
    backgroundColor: 'transparent',
  },
  '& .MuiSelect-select': {
    py: 0,
    pl: 0.25,
    pr: '18px !important',
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
  },
  '& .MuiSelect-icon': {
    color: '#64748b',
    fontSize: '1rem',
    right: 0,
  },
  '&:before, &:after': {
    display: 'none !important',
  },
  '& fieldset': {
    display: 'none',
  },
};

const menuProps = {
  PaperProps: {
    sx: {
      maxHeight: 260,
      borderRadius: '8px',
      boxShadow: '0 4px 16px rgba(0, 0, 0, 0.10)',
      border: '1px solid #e2e8f0',
      mt: 0.5,
      '& .MuiMenuItem-root': {
        fontSize: '0.80rem',
        py: 0.75,
        '&.Mui-selected': {
          backgroundColor: '#f0fdfa',
          fontWeight: 600,
          color: '#0f766e',
          '&:hover': {
            backgroundColor: '#ccfbf1',
          },
        },
      },
    },
  },
};

/**
 * Barra de filtros moderna para listas de jugadores.
 * Permite filtrar por demarcación, lateralidad y rango de año de nacimiento.
 */
function FiltrosLista({
  anosFiltro,
  setAnosFiltro,
  demarcacionesFiltro,
  setDemarcacionesFiltro,
  lateralidadesFiltro,
  setLateralidadesFiltro,
  minAno,
  maxAno,
}) {
  if (!anosFiltro || !setAnosFiltro) {
    return null;
  }

  const handleAnosChange = (event, newValue, activeThumb) => {
    if (!Array.isArray(newValue)) {
      return;
    }
    const minDistance = 0;
    if (activeThumb === 0) {
      setAnosFiltro([Math.min(newValue[0], anosFiltro[1] - minDistance), anosFiltro[1]]);
    } else {
      setAnosFiltro([anosFiltro[0], Math.max(newValue[1], anosFiltro[0] + minDistance)]);
    }
  };

  const hayFiltrosActivos =
    demarcacionesFiltro.length > 0 ||
    lateralidadesFiltro.length > 0 ||
    anosFiltro[0] !== minAno ||
    anosFiltro[1] !== maxAno;

  const handleResetFiltros = () => {
    setDemarcacionesFiltro([]);
    setLateralidadesFiltro([]);
    setAnosFiltro([minAno, maxAno]);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 1.25,
        p: 1.25,
        mb: 2,
        backgroundColor: '#f8fafc',
        borderRadius: '8px',
        border: '1px solid #e2e8f0',
        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.02)',
      }}
    >
      {/* Etiqueta Filtros */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mr: 0.5, color: 'text.secondary' }}>
        <FilterListIcon sx={{ fontSize: '1.05rem', color: '#0f766e' }} />
        <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: 'text.primary' }}>Filtros:</Typography>
      </Box>

      {/* Filtro Demarcación */}
      <FiltroSelectorBox icon={<TbSoccerField />}>
        <Select
          id="demarcacion-select-lista"
          multiple
          value={demarcacionesFiltro}
          onChange={(e) => setDemarcacionesFiltro(e.target.value)}
          displayEmpty
          variant="standard"
          disableUnderline
          renderValue={(selected) => {
            if (!selected || selected.length === 0) {
              return <span style={{ color: '#64748b' }}>Demarcación</span>;
            }
            if (selected.length === 1) {
              return <span style={{ color: '#0f766e' }}>{selected[0]}</span>;
            }
            return <span style={{ color: '#0f766e' }}>{selected.length} posiciones</span>;
          }}
          sx={{
            ...selectCommonSx,
            minWidth: 105,
          }}
          MenuProps={menuProps}
        >
          {demarcaciones.map((dem) => (
            <MenuItem key={dem} value={dem}>
              {dem}
            </MenuItem>
          ))}
        </Select>
      </FiltroSelectorBox>

      {/* Filtro Lateralidad */}
      <FiltroSelectorBox icon={<GiLeg />}>
        <Select
          id="lateralidad-select-lista"
          multiple
          value={lateralidadesFiltro}
          onChange={(e) => setLateralidadesFiltro(e.target.value)}
          displayEmpty
          variant="standard"
          disableUnderline
          renderValue={(selected) => {
            if (!selected || selected.length === 0) {
              return <span style={{ color: '#64748b' }}>Lateralidad</span>;
            }
            if (selected.length === 1) {
              return <span style={{ color: '#0f766e' }}>{selected[0]}</span>;
            }
            return <span style={{ color: '#0f766e' }}>{selected.length} piernas</span>;
          }}
          sx={{
            ...selectCommonSx,
            minWidth: 95,
          }}
          MenuProps={menuProps}
        >
          {lateralidades.map((lat) => (
            <MenuItem key={lat} value={lat}>
              {lat}
            </MenuItem>
          ))}
        </Select>
      </FiltroSelectorBox>

      {/* Filtro Rango de Años */}
      <FiltroSelectorBox icon={<AiFillCalendar />} sx={{ px: 1.25, minWidth: 210 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              fontSize: '0.75rem',
              color: '#334155',
              minWidth: 32,
              textAlign: 'center',
            }}
          >
            {anosFiltro[0]}
          </Typography>
          <Slider
            size="small"
            value={anosFiltro}
            onChange={handleAnosChange}
            disableSwap
            min={minAno}
            max={maxAno}
            sx={{
              width: 90,
              py: 0.5,
              color: '#0f766e',
              '& .MuiSlider-thumb': {
                width: 12,
                height: 12,
                '&:hover, &.Mui-focusVisible': {
                  boxShadow: '0 0 0 6px rgba(15, 118, 110, 0.16)',
                },
              },
              '& .MuiSlider-track': {
                height: 3,
              },
              '& .MuiSlider-rail': {
                height: 3,
                opacity: 0.35,
              },
            }}
          />
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              fontSize: '0.75rem',
              color: '#334155',
              minWidth: 32,
              textAlign: 'center',
            }}
          >
            {anosFiltro[1]}
          </Typography>
        </Box>
      </FiltroSelectorBox>

      {/* Botón para restablecer filtros cuando hay alguno activo */}
      {hayFiltrosActivos && (
        <Tooltip title="Restablecer filtros a valores por defecto" arrow>
          <Button
            size="small"
            onClick={handleResetFiltros}
            startIcon={<ResetIcon sx={{ fontSize: '0.95rem' }} />}
            sx={{
              textTransform: 'none',
              fontSize: '0.75rem',
              fontWeight: 600,
              color: '#64748b',
              height: 32,
              px: 1,
              borderRadius: '6px',
              '&:hover': {
                backgroundColor: '#fee2e2',
                color: '#dc2626',
              },
            }}
          >
            Limpiar filtros
          </Button>
        </Tooltip>
      )}
    </Box>
  );
}

FiltrosLista.propTypes = {
  anosFiltro: PropTypes.arrayOf(PropTypes.number).isRequired,
  setAnosFiltro: PropTypes.func.isRequired,
  demarcacionesFiltro: PropTypes.arrayOf(PropTypes.string).isRequired,
  setDemarcacionesFiltro: PropTypes.func.isRequired,
  lateralidadesFiltro: PropTypes.arrayOf(PropTypes.string).isRequired,
  setLateralidadesFiltro: PropTypes.func.isRequired,
  minAno: PropTypes.number.isRequired,
  maxAno: PropTypes.number.isRequired,
};

export default FiltrosLista;
